// Entries and exits as lines: a running total (default) or people per interval,
// with gates open and game start marked.
import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from 'chart.js';
import { ChartHeader, ChartSegmented, ResolutionToggle, applyChartTheme, CHART_THEME, hoverGuidePlugin, sectionLineDataset } from '../../Concession/charts/chartTheme';
import { PHASES } from '../../Retail/retailPalette';
import { intervalFlow, minuteToClock, clockToMinute } from '../gatesMetrics';

ChartJS.register(annotationPlugin);

// Entries green, exits red (like the retail stairs: up green, down red)
const ENTRIES_COLOR = '#16a34a';
const EXITS_COLOR = '#dc2626';

const INTERVALS = [1, 5, 15, 30, 60];
const MODES = [
  { value: 'total', label: 'Running total' },
  { value: 'interval', label: 'Per interval' },
];

const shortClock = (minute) => minuteToClock(minute).replace(':00 ', ' ');

// Dashed vertical marker at a game-day minute; x is a (fractional) point index
const markAt = (x, label, color) => ({
  type: 'line',
  scaleID: 'x',
  value: x,
  borderColor: color,
  borderWidth: 1.5,
  borderDash: [6, 4],
  label: {
    display: true,
    content: label,
    position: 'start',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color,
    font: { family: CHART_THEME.font, size: 11, weight: '600' },
    padding: { x: 6, y: 2 },
  },
});

// `range` ({ start, end } in game-day minutes) limits the chart to the analysis window; the running
// total then starts from zero at the window's start, matching the window's numbers above.
// `who` names what the people went through ("all gates", or a gate's name) for the subtitle.
const GatesFlowChart = ({ series, currentGame, range = null, who = 'all gates', title = 'Entries and exits', height = 300 }) => {
  const [mode, setMode] = useState('total');
  const [interval, setIntervalSize] = useState(1);
  const isTotal = mode === 'total';
  const size = isTotal ? 1 : interval;

  const points = useMemo(() => {
    const all = intervalFlow(series || [], size);
    const buckets = range ? all.filter((b) => b.end > range.start && b.start < range.end) : all;
    if (!isTotal) return buckets.map((b) => ({ ...b, at: b.start }));
    // Running total as of the end of each minute
    let sumIn = 0;
    let sumOut = 0;
    return buckets.map((b) => {
      sumIn += b.in;
      sumOut += b.out;
      return { ...b, in: sumIn, out: sumOut, at: b.end };
    });
  }, [series, size, range, isTotal]);

  const marks = useMemo(() => {
    if (!points.length) return [];
    const first = points[0].at;
    const last = points[points.length - 1].at;
    const toX = (minute) => (minute - first) / size;
    // Allow one step before the first point (a window starting right at gates open still shows it)
    const inRange = (minute) => minute != null && minute >= first - size && minute <= last;
    const gates = clockToMinute(currentGame?.gatesOpen);
    const start = clockToMinute(currentGame?.gameStartTime);
    return [
      ...(inRange(gates) ? [markAt(toX(gates), 'Gates open', PHASES.gates.color)] : []),
      ...(inRange(start) ? [markAt(toX(start), 'Game start', CHART_THEME.gameStart)] : []),
    ];
  }, [points, size, currentGame?.gatesOpen, currentGame?.gameStartTime]);

  const data = useMemo(() => ({
    labels: points.map((p) => p.at),
    datasets: [
      { label: 'Entries', data: points.map((p) => p.in), ...sectionLineDataset(ENTRIES_COLOR) },
      { label: 'Exits', data: points.map((p) => p.out), ...sectionLineDataset(EXITS_COLOR) },
    ],
  }), [points]);

  const options = useMemo(() => applyChartTheme({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          // Hourly labels, whatever the interval
          callback: (value, index) => {
            const at = points[index]?.at;
            if (at == null) return '';
            return at % 60 === 0 ? shortClock(at) : '';
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, callback: (v) => Number(v).toLocaleString() },
        title: { display: true, text: isTotal ? 'People so far' : `People per ${interval} min` },
      },
    },
    plugins: {
      // Legend is drawn in HTML above the chart (Chart.js measured it before the font loaded and clipped Exits)
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const p = points[items[0]?.dataIndex];
            if (!p) return '';
            return isTotal ? `As of ${minuteToClock(p.at)}` : `${minuteToClock(p.start)} – ${minuteToClock(p.end)}`;
          },
          label: (ctx) => ` ${ctx.dataset.label}: ${Number(ctx.raw).toLocaleString()}`,
        },
      },
      annotation: { annotations: marks },
    },
  }), [points, isTotal, interval, marks]);

  const subtitle = isTotal
    ? `Running total of people through ${who}${range ? ' since the start of the window' : ''}.`
    : `People through ${who} in each ${interval}-minute interval.`;

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title={title}
        subtitle={subtitle}
        controls={(
          <>
            <ChartSegmented options={MODES} value={mode} onChange={setMode} />
            {!isTotal && <ResolutionToggle resolutions={INTERVALS} value={interval} onChange={setIntervalSize} />}
          </>
        )}
      />
      {points.length ? (
        <>
        <div className="gx-legend">
          <span><i style={{ background: ENTRIES_COLOR }} />Entries</span>
          <span><i style={{ background: EXITS_COLOR }} />Exits</span>
        </div>
        <div style={{ position: 'relative', height }}>
          <Line data={data} options={options} plugins={[hoverGuidePlugin]} />
        </div>
        </>
      ) : (
        <div className="gx-chart-empty">No entry or exit readings{range ? ' in the selected window' : ' for this game'}.</div>
      )}
    </div>
  );
};

export default GatesFlowChart;
